import React, { useEffect } from 'react';
import { useStore } from '../../../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../../services/general/error';
import GeneralService from '../../../../../../../services/general';

// props
import { IVesselRequisitionItemPricingResourceProps, IVesselRequisitionItemResourceProps, IVesselRequisitionItemResourceShortProps } from '../../../../../../../props/vessels/requisitions/item';

// components
import { Checkbox, DefaultButton, DetailsList, Dropdown, IColumn, IconButton, Panel, PanelType, PrimaryButton, SelectionMode, Spinner, SpinnerSize, Stack, TextField, TooltipHost } from '@fluentui/react';
import VesselRequisitionItemPricingStatus from '../../../../../../../manifests/status/vesselRequisitionItemPricing';
import Label from '../../../../../../typography/label';
import Text from '../../../../../../typography/text';
import Details from '../../../../../../typography/details';
import ValidationService from '../../../../../../../services/validation';
import SelectVendor from '../../../../../../uiframeworks/forms/operationals/vendors/vendor';
import OutcomeService from '../../../../../../../services/finance/outcomes';
import { PPhPercentageOptions, PPNPercentageOptions } from '../../../../../../../manifests/taxPercentage';

interface UpdateVesselRequisitionItemPricingDetailsProps {
    pricing: Partial<IVesselRequisitionItemPricingResourceProps>;
    onDismissed(pricing?: Partial<IVesselRequisitionItemPricingResourceProps>): void;
}

const UpdateVesselRequisitionItemPricingDetails: React.FC<UpdateVesselRequisitionItemPricingDetailsProps> = (props: UpdateVesselRequisitionItemPricingDetailsProps) => {
    const { banner, user } = useStore();

    const [pricings, setPricings] = React.useState<Partial<IVesselRequisitionItemPricingResourceProps>[]>([]);
    const [data, setData] = React.useState<Partial<IVesselRequisitionItemPricingResourceProps>>(props.pricing);
    const [error, setError] = React.useState<any>({});

    const renderItemDetails = () => {
        if (data.item) {
            let size: JSX.Element | null = null;
            let weight: JSX.Element | null = null;

            // get size details
            const height = Number((data.item.goods.height || "").trim());
            const width = Number((data.item.goods.width || "").trim());
            const length = Number((data.item.goods.length || "").trim());
            const diameter = Number((data.item.goods.diameter || "").trim());

            if (diameter !== 0) {
                size = <Text size={'small'}>Diameter: {GeneralService.getNumberWithSeparator(Number(diameter))} cm</Text>
            } else if (height !== 0 && width !== 0 && length !== 0) {
                size = <Text size={'small'}>Size (P x L x T): {GeneralService.getNumberWithSeparator(Number(length))} cm x {GeneralService.getNumberWithSeparator(Number(width))} cm x {GeneralService.getNumberWithSeparator(Number(height))} cm</Text>
            }

            // get weight details
            const weightAmount = Number((data.item.goods.weightAmount || "").trim());

            if (weightAmount !== 0) {
                weight = <Text size={'small'}>Weight: {GeneralService.getNumberWithSeparator(Number(weightAmount))} {data.item.goods.weightAmount}</Text>
            }

            return <>
                <Details title={"Item name"} text={data.item.goods.name} />
                {size}
                {weight}
                <Details title={"Quantity"} text={`${GeneralService.getNumberWithSeparator(Number(data.item.quantityAmount))} ${data.item.quantityUnit?.name}`} />
            </>
        }

        return null;
    }

    const updatePricing = (_data: Partial<IVesselRequisitionItemPricingResourceProps>) => {
        const totalPrice = Number(_data.price) * Number(_data.item?.quantityAmount || "0");
        const details = {
            originalAmount: totalPrice + "",
            includePPh: _data.includePPh,
            includePPn: _data.includePPn,
            pph: _data.pph,
            ppn: _data.ppn,
            ppnPercentage: _data.pphPercentage,
            pphPercentage: _data.ppnPercentage
        }

        const _price = OutcomeService.calculatePrice(details);
        _data.ppn = details.ppnPercentage !== 'custom' ? ((_price.ppn || 0) + "") : ((_price.ppn || "") + "");
        _data.pph = details.pphPercentage !== 'custom' ? ((_price.pph || 0) + "") : ((_price.pph || "") + "");
        _data.finalPrice = (_price.totalAmount || 0) + "";
        _data.invoicePrice = (_price.amount || 0) + "";
        _data.totalPrice = totalPrice + "";

        return _data;
    }

    return <Panel headerText={'Update Requisition Item Price'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed()}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text={"Update"} onClick={() => props.onDismissed(data)} />
                <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed() }} />
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {renderItemDetails()}
            <Stack className={'divider'} />
            <SelectVendor label='Vendor name'
                required={true}
                selected={data.vendor}
                onChange={(selected) => {
                    const _data = data;
                    _data.vendor = selected;

                    const validation = ValidationService.combination(selected, ['required'], {});
                    error.vendor = validation.message;

                    setData({ ..._data });
                }} />
            <TextField label={"Price per quantity"}
                prefix='Rp'
                required={true}
                value={(data.price === undefined ? "" : data.price) + ""}
                onChange={(evt, value) => {
                    if ((value || "").trim() === "" || !isNaN(Number(value))) {
                        let _data = data;

                        _data.price = value !== "" ? (value || "") : "";

                        const validation = ValidationService.combination(value, ['required', 'limit', 'number'], { maxChars: 20 });
                        error.amount = validation.message;

                        _data = updatePricing(_data);
                        setData({ ...data });
                        setError({ ...error });
                    }
                }}
                errorMessage={error.price} />
            <Stack className={'divider'} />
            <Stack tokens={{ childrenGap: 20 }} horizontal>
                <Stack.Item>
                    <Checkbox checked={data.includePPn}
                        label='Include PPn'
                        onChange={(ev, checked) => {
                            let _data = data;
                            _data.includePPn = checked;

                            /*const _price = OutcomeService.calculatePrice(data);
                            data.totalAmount = (_price.totalAmount || 0) + "";
                            data.amount = (_price.amount || 0) + "";

                            const _orders = manipulateOrderPrice(data);
                            data.orders = _orders;*/

                            _data = updatePricing(_data);
                            setData({ ..._data });
                        }} />
                </Stack.Item>
                <Stack.Item>
                    <Checkbox checked={data.includePPh}
                        label='Include PPh'
                        onChange={(ev, checked) => {
                            let _data = data;
                            _data.includePPh = checked;

                            /*const _price = OutcomeService.calculatePrice(data);
                            data.totalAmount = (_price.totalAmount || 0) + "";
                            data.amount = (_price.amount || 0) + "";

                            const _orders = manipulateOrderPrice(data);
                            data.orders = _orders;*/

                            _data = updatePricing(_data);
                            setData({ ...data });
                        }} />
                </Stack.Item>
            </Stack>
            <Stack tokens={{ childrenGap: 2 }}>
                <Label size={'xsmall'} required>PPn</Label>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item>
                        <Dropdown options={PPNPercentageOptions}
                            selectedKey={data.ppnPercentage}
                            onChange={(evt, opt) => {
                                let _data = data;
                                if (opt && opt.key !== 'custom') {
                                    _data.ppn = ((Number(opt.key) / 100 * Number(_data.totalPrice)).toFixed());
                                }
                                _data.ppnPercentage = (opt?.key || "") as string;

                                _data = updatePricing(_data);
                                setData({ ..._data });
                            }}
                            styles={{ root: { width: 100 } }} />
                    </Stack.Item>
                    <Stack.Item grow={1}>
                        <TextField value={data.ppnPercentage !== "custom" ? GeneralService.getNumberWithSeparator((data.ppn || "0") + "") : (data.ppn) + ""}
                            disabled={data.ppnPercentage !== "custom"}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value)) || (value || "") === "") {
                                    let _data = data;
                                    _data.ppn = (value || "");

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.ppn = validation.message;

                                    _data = updatePricing(_data);
                                    setData({ ..._data });
                                    setError({ ...error });
                                }
                            }}
                            prefix={"Rp"}
                            errorMessage={error.ppn} />
                    </Stack.Item>
                </Stack>
            </Stack>
            <Stack tokens={{ childrenGap: 2 }}>
                <Label size={'xsmall'} required>PPh</Label>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item>
                        <Dropdown options={PPhPercentageOptions}
                            selectedKey={data.pphPercentage}
                            onChange={(evt, opt) => {
                                let _data = data;
                                if (opt && opt.key !== 'custom') {
                                    _data.pph = ((Number(opt.key) / 100 * Number(_data.totalPrice)).toFixed());
                                }
                                _data.pphPercentage = (opt?.key || "") as string;

                                /*const _price = OutcomeService.calculatePrice(data);
                                data.totalAmount = (_price.totalAmount || 0) + "";
                                data.amount = (_price.amount || 0) + "";

                                const _orders = manipulateOrderPrice(data);
                                data.orders = _orders;*/

                                _data = updatePricing(_data);
                                setData({ ..._data });
                            }}
                            styles={{ root: { width: 100 } }} />
                    </Stack.Item>
                    <Stack.Item grow={1}>
                        <TextField value={data.pphPercentage !== "custom" ? GeneralService.getNumberWithSeparator((data.pph || "0") + "") : (data.pph) + ""}
                            disabled={data.pphPercentage !== "custom"}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value)) || (value || "") === "") {
                                    let _data = data;
                                    _data.pph = (value || "");

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.pph = validation.message;

                                    _data = updatePricing(_data);
                                    setData({ ..._data });
                                    setError({ ...error });
                                }
                            }}
                            prefix={"Rp"}
                            errorMessage={error.pph} />
                    </Stack.Item>
                </Stack>
            </Stack>
            <Stack className={'divider'}></Stack>
            <Stack horizontal tokens={{ childrenGap: 20 }} horizontalAlign='space-between'>
                <Stack styles={{ root: { width: 300 } }}>
                    <Label size={'xsmall'}>Total Price</Label>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(data.totalPrice || 0)}</Text>
                </Stack>
                <Stack styles={{ root: { marginTop: 20 } }}>
                    <Text size={'xsmall'}>({GeneralService.convertNumberToWords(Number(data.totalPrice || "0"))})</Text>
                </Stack>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 20 }} horizontalAlign='space-between'>
                <Stack styles={{ root: { width: 300 } }}>
                    <Label size={'xsmall'}>Total price paid to vendor</Label>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(data.invoicePrice || 0)}</Text>
                </Stack>
                <Stack styles={{ root: { marginTop: 20 } }}>
                    <Text size={'xsmall'}>({GeneralService.convertNumberToWords(Number(data.invoicePrice || "0"))})</Text>
                </Stack>
            </Stack>
        </Stack>
    </Panel>
};

export default UpdateVesselRequisitionItemPricingDetails;
